import { Divider } from "$root/components/Divider";
import type { PageSplit, PageSplitRatio } from "$root/functional-areas/portfolio-lab/ctx";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { ButtonGroupRadio, Icon, Row } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type React from "react";
import styles from "./PortfolioLabWidgetToolbar.module.css";

export type PortfolioLabWidgetToolbarProps = {
	/** Whether any loaded chart is out of sync with the current composition (enables "Update Charts"). */
	needToReloadChart: boolean;
	/** Recompute metrics for every loaded investment and refresh the charts. */
	onReloadMetrics: () => Promise<void>;
	/** Current page split ratio — drives the selected state of the split-ratio control. */
	pageSplit: PageSplit;
	/** Change the left/right column split. */
	onPageSplitChange: (next: PageSplitRatio) => void;
	/** Open the lab's Display Settings side panel. */
	onOpenSettings: () => void;
} & Omit<React.ComponentPropsWithoutRef<"div">, "onChange">;

/**
 * The widget-panel toolbar for Portfolio Lab: "Update Charts" on the left, then the column-split
 * control and "Display Settings" on the right. Layout-agnostic — the page decides where it sits in
 * the grid (and reflows it to the top in single-column mode). Its one internal responsive concern is
 * the split-ratio control, which is meaningless in a single column and hides itself there.
 */
export function PortfolioLabWidgetToolbar({
	needToReloadChart,
	onReloadMetrics,
	pageSplit,
	onPageSplitChange,
	onOpenSettings,
	className,
	...rest
}: PortfolioLabWidgetToolbarProps): React.JSX.Element {
	return (
		<div className={`bg-white rounded-sm px-2 py-2 relative ${className ?? ""}`} {...rest}>
			<Row flexGrow={0} alignItems="center" justifyContent="space-between">
				<Row flexGrow={0} alignItems="center" gap={12}>
					<Button palette="secondary" disabled={!needToReloadChart} size="x-small" onClickAsync={onReloadMetrics}>
						<Icon
							icon="Reload"
							size={18}
							color={needToReloadChart ? themeCSSVars.palette_P500 : themeCSSVars.palette_N500}
							className="mr-0.5"
						/>
						Update Charts
					</Button>
				</Row>
				<Row flexGrow={0} flexShrink={0} gap={8} alignItems="center">
					<div className={styles.splitControls} data-qualifier="PortfolioLab/Toolbar/PageSplit">
						<ButtonGroupRadio<PageSplitRatio>
							size="x-small"
							options={[
								{ value: "1/3", children: <Icon icon="screen-size2" size={18} /> },
								{ value: "1/2", children: <Icon icon="screen-size1" size={18} /> },
								{ value: "2/3", children: <Icon icon="screen-size3" size={18} /> },
							]}
							value={pageSplit.kind === "ratio" ? pageSplit.width : null}
							onChange={onPageSplitChange}
						/>
					</div>
					<Divider direction="vertical" className="h-6" />
					<Button palette="ghost" size="x-small" onClick={onOpenSettings}>
						<Icon icon="Settings" size={18} color={themeCSSVars.palette_N500} className="mr-0.5" />
						Display Settings
					</Button>
				</Row>
			</Row>
		</div>
	);
}
